/**
 * @author michalstolarski
 * @since 2023-3-8
 */

import * as React from "react";
import styled from "styled-components";

import { rem } from "@ui/helpers";
import { CheckWithSpace, Close } from "@ui/Assets/Symbolicons";
import { TestWrapper } from "@ui/Atoms/TestWrapper";

import { Markdown } from "@containers/Markdown";

interface RowProps {
  label: string;
  values: boolean[];
}

interface Props {
  testId?: string;
  className?: string;
  headers: string[];
  rows: RowProps[];
}

const WhatIsIncludedTable = (props: Props): React.ReactElement => {
  return (
    <TestWrapper testId={props.testId}>
      <Container className={props.className}>
        <Table>
          <thead>
            <tr>
              <EmptyHeaderCell />
              {props.headers.map((label, index) => (
                <HeaderCell key={`table-header-${index}`}>
                  <Markdown>{label}</Markdown>
                </HeaderCell>
              ))}
            </tr>
          </thead>

          <tbody>
            {props.rows.map((row, index) => (
              <Row key={`table-row-${index}`}>
                <LabelCell>
                  <Markdown>{row.label}</Markdown>
                </LabelCell>

                {row.values.map((value, index) => (
                  <td key={`table-cell-${index}`}>
                    {value ? (
                      <CheckIcon variant="line" />
                    ) : (
                      <CrossIcon variant="line" />
                    )}
                  </td>
                ))}
              </Row>
            ))}
          </tbody>
        </Table>
      </Container>
    </TestWrapper>
  );
};

export { RowProps, WhatIsIncludedTable };

const Container = styled.div`
  --what-included-bg: ${({ theme }) => theme.palette.white};
  --what-included-label-width: ${rem(110)};
  --what-included-icon-size: ${props => props.theme.icons.sizes.base_x2};
  --what-included-cell-min-height: calc(
    (
        ${props =>
          `${props.theme.fonts.lineHeights.caption} + ${props.theme.margins.base}`}
      ) * 2
  );
  --what-included-cell-padding: ${props => props.theme.margins.base};

  position: relative;
  width: 100%;
  overflow-x: auto;

  ${props => props.theme.breakpoints.mobile} {
    --what-included-label-width: ${rem(150)};
  }
`;

const Table = styled.table`
  min-width: 100%;
  text-align: center;
  background-color: var(--what-included-bg);
`;

const Row = styled.tr`
  border-bottom: 1px solid ${props => props.theme.palette.grey100};

  &:last-child {
    border-bottom: none;
  }
`;

const HeaderCell = styled.th`
  min-width: ${rem(80)};
  min-height: var(--what-included-cell-min-height);
  padding: var(--what-included-cell-padding);
  font-size: ${props => props.theme.fonts.sizes.body2};
  font-weight: ${props => props.theme.fonts.weights.semibold};
`;

const EmptyHeaderCell = styled.th`
  position: sticky;
  left: 0;
  min-width: var(--what-included-label-width);
  background-color: var(--what-included-bg);
  z-index: 1;
`;

const LabelCell = styled.td`
  position: sticky;
  left: 0;
  height: var(--what-included-cell-min-height);
  padding: var(--what-included-cell-padding);
  padding-left: 0;
  font-size: ${props => props.theme.fonts.sizes.caption};
  line-height: ${props => props.theme.fonts.lineHeights.caption};
  color: ${props => props.theme.palette.grey600};
  text-align: left;
  background-color: var(--what-included-bg);
  z-index: 1;
`;

const CheckIcon = styled(CheckWithSpace)`
  width: var(--what-included-icon-size);
  height: var(--what-included-icon-size);
  border-radius: 100%;
  background-color: ${props => props.theme.palette.blue100};
  overflow: hidden;

  * {
    fill: ${props => props.theme.palette.blue900};
  }
`;

const CrossIcon = styled(Close)`
  width: var(--what-included-icon-size);
  height: var(--what-included-icon-size);

  * {
    fill: ${props => props.theme.palette.grey300};
  }
`;
